
import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { X, Receipt, Search, Filter } from 'lucide-react';
import { StatementItem } from '@/types';

interface PointsStatementModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const ACTION_TRANSLATIONS: Record<string, string> = {
    'daily_login': 'Login Diário',
    'complete_signup': 'Cadastro Completo',
    'referral_signup': 'Indicação de Amigo(a)',
    'first_link_generated': 'Primeiro Link otimizado',
    'first_store_redemption': 'Primeiro Resgate na Lojinha',
    'first_approved_request': 'Primeira Aprovação de Pontos',
    'weekly_mission_completed': 'Missão Semanal',
    'birthday_bonus': 'Bônus de Aniversário',
    'campaign_bonus': 'Bônus de Campanha',
    'reward_redemption': 'Resgate de Recompensa',
    'manual_earning': 'Bônus por Compra',
    'monthly_level_bonus': 'Bônus Mensal de Nível',
    'adjustment': 'Ajuste de Saldo',

    // Mission References
    'mission:generate_5_links': 'Otimizar 5 Links',
    'mission:login_streak_7d_bonus': '7 dias consecutivos de login',

    // Level References
    'level:bronze': 'Bronze',
    'level:prata': 'Prata',
    'level:ouro': 'Ouro',
    'level:diamante': 'Diamante',
};

export const PointsStatementModal = ({ isOpen, onClose }: PointsStatementModalProps) => {
    const [items, setItems] = useState<StatementItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(0);

    // Refs for control
    const observerTarget = useRef<HTMLDivElement>(null);
    const initialLoadRef = useRef(false);

    const ITEMS_PER_PAGE = 15;

    useEffect(() => {
        if (isOpen) {
            // Only fetch if we haven't fetched for this open session yet (Strict Mode fix + Logic)
            if (!initialLoadRef.current) {
                resetAndFetch();
                initialLoadRef.current = true;
            }
        } else {
            // Reset ref when closed so it fetches again next time
            initialLoadRef.current = false;
        }
    }, [isOpen]);

    // Observer for infinite scroll
    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting && hasMore && !loading) {
                    fetchPage(page + 1);
                }
            },
            { threshold: 0.1 }
        );

        if (observerTarget.current) {
            observer.observe(observerTarget.current);
        }

        return () => observer.disconnect();
    }, [hasMore, loading, page]);

    const resetAndFetch = () => {
        setItems([]);
        setPage(0);
        setHasMore(true);
        fetchPage(0);
    };

    const fetchPage = async (pageToFetch: number) => {
        setLoading(true);
        try {
            const from = pageToFetch * ITEMS_PER_PAGE;
            const to = from + ITEMS_PER_PAGE - 1;

            const { data, error } = await supabase
                .from('my_points_statement')
                .select('*')
                .order('created_at', { ascending: false })
                .range(from, to);

            if (error) throw error;

            const newItems = data || [];

            if (newItems.length < ITEMS_PER_PAGE) {
                setHasMore(false);
            }

            setItems(prev => pageToFetch === 0 ? newItems : [...prev, ...newItems]);
            setPage(pageToFetch);

        } catch (err) {
            console.error('Error fetching statement:', err);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('pt-BR', {
            timeZone: 'America/Sao_Paulo',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="relative w-full max-w-lg bg-white dark:bg-gray-900 rounded-3xl shadow-2xl border border-white/20 dark:border-gray-800 overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[85vh]">

                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between bg-gray-50/50 dark:bg-gray-800/50">
                    <div className="flex items-center gap-2">
                        <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-full text-purple-600 dark:text-purple-400">
                            <Receipt className="w-5 h-5" />
                        </div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white">Extrato de Pontos</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body - Scrollable List */}
                <div className="flex-1 overflow-y-auto p-0 scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-800">
                    {items.length === 0 && !loading ? (
                        <div className="flex flex-col items-center justify-center p-12 text-center h-full">
                            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                                <Receipt className="w-8 h-8 text-gray-400" />
                            </div>
                            <p className="text-gray-500 font-medium">Nenhuma movimentação ainda.</p>
                            <p className="text-sm text-gray-400 mt-1">
                                Complete missões para começar a ganhar pontos!
                            </p>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-100 dark:divide-gray-800">
                            {items.map((item) => {
                                const isPositive = item.points > 0;
                                const actionLabel = ACTION_TRANSLATIONS[item.action_code] || item.action_code;
                                const referenceLabel = item.action_reference ? (ACTION_TRANSLATIONS[item.action_reference] || item.action_reference) : null;

                                return (
                                    <div key={item.id} className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors flex items-center justify-between group">
                                        <div className="flex flex-col gap-0.5 kind-of-tight">
                                            <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                                {actionLabel} {referenceLabel && <span className="font-normal text-gray-500 dark:text-gray-400 text-xs">({referenceLabel})</span>}
                                            </p>
                                            <p className="text-xs text-gray-400 font-mono">
                                                {formatDate(item.created_at)}
                                            </p>
                                        </div>
                                        <div className="flex flex-col items-end gap-1">
                                            <div className={`font-bold font-mono text-sm ${isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                                                {isPositive ? '+' : ''}{item.points}
                                            </div>
                                            <div className="flex items-center gap-1 text-[10px] text-gray-400 font-mono leading-none" title="Pontos base x Multiplicador do Nível">
                                                {item.base_points && item.applied_multiplier && item.applied_multiplier > 1 && (
                                                    <span className="line-through">{item.base_points}</span>
                                                )}
                                                {item.applied_multiplier && (
                                                    <span>({item.applied_multiplier}x)</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}

                            {/* Loading Sentinel */}
                            {hasMore && (
                                <div ref={observerTarget} className="py-6 text-center text-xs text-gray-400 flex items-center justify-center gap-2">
                                    {loading && <div className="w-3 h-3 rounded-full border-2 border-gray-400 border-t-transparent animate-spin"></div>}
                                    {loading ? 'Carregando mais...' : ''}
                                </div>
                            )}

                            {!hasMore && items.length > 0 && (
                                <div className="py-6 text-center text-xs text-gray-400 bg-gray-50/30 dark:bg-gray-800/20">
                                    Fim do extrato
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-3 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50 text-center text-[10px] text-gray-400">
                    Mostrando {items.length} movimentações no total
                </div>
            </div>
        </div>
    );
};
